import { useEffect, useState } from 'react';

import {
    ArrowForwardIosRounded,
    ArrowBackIosRounded,
    AutoGraph,
    ContactPageOutlined,
    ExpandLess,
    ExpandMore,
    PaidOutlined,
    SettingsOutlined,
    SupervisedUserCircleOutlined,
    SwapCalls,
} from '@mui/icons-material';
import { Box, Collapse, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, Link } from 'react-router-dom';

import CuredLogoNoText from 'assets/images/svg/cured-logo-green-no-text.svg';
import CuredLogoWithText from 'assets/images/svg/cured-logo-green.svg';
import { useCustomAuth } from 'auth/AuthContext';
import Button from 'domains/core/components/Button';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import { URLPaths } from 'models/enums';
import { maximizeLeftNavPanel, minimizeLeftNavPanel } from 'state/appVisual/appVisualActions';

import ContentIcon from '../SvgIcons/ContentIcon';

import NavigationButton, { NavigationButtonVariant } from './NavigationButton';
import ProfileButton from './ProfileButton';
import { shouldShowSettingsNav } from './shouldShowSettingsNavForFlags';

export const NavigationPanel = () => {
    const dispatch = useDispatch();
    const isLeftNavPanelMaximized = useSelector((state: any) => state.appVisual.isLeftNavPanelMaximized);

    const { isAuthenticated, user } = useCustomAuth();
    const { data: featureSettings, isSuccess: isFeatureSettingsSuccess } = useFeatureSettings({
        enabled: isAuthenticated,
    });
    const {
        accessControlLayer: accessControlLayerFlag,
        contentOnly: hasContentOnlyFlag,
        aiBrandTools: hasAiBrandToolsFlag,
        customerSupport: hasCustomerSupportFlag,
        accessCenter: hasAccessCenterFlag,
    } = useFlags();

    const {
        canViewAccessManagement,
        canViewContent,
        canAccessContacts,
        canViewAudiences,
        canViewCurations,
        canViewValue,
        canViewInsightsDashboards,
        canRegisterAiBrandVoice,
        isAccessCentrePermissionsLoading,
    } = useAccessCentrePermissions();

    const accessCentreNavReady = !isAccessCentrePermissionsLoading;

    const isFlagReady =
        isFeatureSettingsSuccess &&
        hasContentOnlyFlag !== undefined &&
        accessControlLayerFlag !== undefined &&
        hasAccessCenterFlag !== undefined;

    /**
     * Legacy (Access Centre off): show item. When AC is on, wait for `isFlagReady` and /me so we never
     * flash "full" nav — `hasAccessCenterFlag !== true` was true while LD was still undefined.
     */
    const showNavIfPermitted = (can: boolean) =>
        isFlagReady && (hasAccessCenterFlag !== true ? true : accessCentreNavReady && can);

    const hasAccessControlLayer = featureSettings?.acl_enabled && accessControlLayerFlag;
    const hasContentOnly = featureSettings?.content_only_mode_enabled && hasContentOnlyFlag;
    /** When Access Centre is on: AI brand path needs LD flag + `ai.brand_registration` (and /me ready). */
    const aiBrandEligible = accessCentreNavReady && hasAiBrandToolsFlag === true && canRegisterAiBrandVoice;

    const showSettingsNav =
        isFlagReady &&
        shouldShowSettingsNav({
            accessCenterFlag: hasAccessCenterFlag,
            aiBrandToolsFlag: hasAiBrandToolsFlag,
            customerSupportFlag: hasCustomerSupportFlag,
            accessCentreEligible: accessCentreNavReady && canViewAccessManagement,
            contentOnlyMode: hasContentOnly,
            aiBrandEligible,
        });

    /** Users / Roles links are Access Centre–only; Brand setup uses AI flag + permission when AC is on. */
    const showUsersAndRolesInSettingsNav =
        hasAccessCenterFlag === true && accessCentreNavReady && canViewAccessManagement;

    /** Legacy: AI brand flag only. Gated: Access Centre flag + AI flag + `ai.brand_registration`. */
    const showBrandSetupInSettingsNav = hasAccessCenterFlag === true ? aiBrandEligible : hasAiBrandToolsFlag === true;

    const showContentNavItem = showNavIfPermitted(canViewContent);
    const showCurationsNavItem = showNavIfPermitted(canViewCurations);
    const showAudiencesNavItem = showNavIfPermitted(canViewAudiences);
    const showContactsNavItem = showNavIfPermitted(canAccessContacts);
    const showValueNavItem = showNavIfPermitted(canViewValue);
    const showInsightsNavItem = showNavIfPermitted(canViewInsightsDashboards);

    const history = useHistory();
    const location = useLocation();
    const [isHover, setIsHover] = useState(false);
    const [settingsExpanded, setSettingsExpanded] = useState(false);
    const isSettingsPath = location.pathname.startsWith('/settings');

    useEffect(() => {
        if (isSettingsPath) setSettingsExpanded(true);
    }, [isSettingsPath]);

    if (!isAuthenticated) {
        return null;
    }

    return (
        <>
            {/* Overlay of main page body when nav panel is expanded */}
            <Box
                hidden={!isLeftNavPanelMaximized}
                bgcolor="#95a6a733"
                position="fixed"
                left={0}
                top={0}
                width="100%"
                height="100%"
                zIndex={10}
                onClick={() => dispatch(minimizeLeftNavPanel(false))}
            />
            <Box hidden={!isLeftNavPanelMaximized}>
                {/* navigation panel when maximized */}
                <Box
                    height="100%"
                    width="15.25rem"
                    sx={{
                        backgroundColor: 'secondary.light',
                        borderLeftColor: 'secondary.light',
                        borderLeftStyle: 'solid',
                        borderLeftWidth: '0.5rem',
                    }}
                    position="fixed"
                    left={0}
                    top={0}
                    zIndex={11}
                    overflow="hidden"
                    flex={1}
                >
                    <List dense disablePadding>
                        <ProfileButton userEmail={user.email} />
                        <Box
                            display="flex"
                            flexDirection="row"
                            marginTop={2}
                            marginLeft={2}
                            marginRight={2}
                            marginBottom={10}
                        >
                            <Link to="/">
                                <img
                                    src={CuredLogoWithText}
                                    alt="Cured logo"
                                    height="32"
                                    width="100%"
                                    onClick={() => dispatch(minimizeLeftNavPanel(false))}
                                />
                            </Link>
                        </Box>
                        {!hasContentOnly && showCurationsNavItem && (
                            <NavigationButton
                                title="curations"
                                path={URLPaths.CURATIONS}
                                helperText="Setup, test, and schedule interactions."
                                icon={<SwapCalls />}
                                variant={NavigationButtonVariant.WIDE}
                            />
                        )}
                        {!hasAccessControlLayer && !hasContentOnly && showAudiencesNavItem && (
                            <NavigationButton
                                title="audiences"
                                path={URLPaths.SEGMENTS}
                                helperText="Filter contacts to create new audiences."
                                icon={<SupervisedUserCircleOutlined />}
                                variant={NavigationButtonVariant.WIDE}
                            />
                        )}
                        {!hasAccessControlLayer && !hasContentOnly && showContactsNavItem && (
                            <NavigationButton
                                title="contacts"
                                path={URLPaths.CONTACTS}
                                helperText="Browse existing contacts and upload new ones."
                                icon={<ContactPageOutlined />}
                                variant={NavigationButtonVariant.WIDE}
                            />
                        )}
                        {showContentNavItem && (
                            <NavigationButton
                                title="content"
                                path={URLPaths.CONTENT}
                                helperText="Create your own content or customize a template to fit your brand."
                                icon={<ContentIcon />}
                                variant={NavigationButtonVariant.WIDE}
                                dataTestId="content-button-list-item"
                                ariaLabel="Open content"
                            />
                        )}
                        {!hasContentOnly && showValueNavItem && (
                            <NavigationButton
                                title="value"
                                path={URLPaths.VALUE}
                                helperText="Track the impact of your marketing efforts."
                                icon={<PaidOutlined />}
                                variant={NavigationButtonVariant.WIDE}
                            />
                        )}
                        {!hasContentOnly && showInsightsNavItem && (
                            <NavigationButton
                                title="insights"
                                path={URLPaths.INSIGHTS}
                                helperText="Monitor the performance of Curations and gather insights on contacts."
                                icon={<AutoGraph />}
                                variant={NavigationButtonVariant.WIDE}
                            />
                        )}
                        {showSettingsNav && (
                            <>
                                <ListItem
                                    button
                                    onClick={() => setSettingsExpanded(!settingsExpanded)}
                                    sx={{
                                        color: isSettingsPath ? 'secondary.main' : 'primary.main',
                                        height: '3.25rem',
                                        '&:hover': { color: 'action.active' },
                                    }}
                                    data-testid="access-center-nav-settings"
                                    role="button"
                                    aria-expanded={settingsExpanded}
                                    aria-haspopup="true"
                                >
                                    <ListItemIcon sx={{ color: 'inherit' }}>
                                        <SettingsOutlined />
                                    </ListItemIcon>
                                    <ListItemText
                                        sx={{ whiteSpace: 'nowrap' }}
                                        disableTypography
                                        primary={
                                            <Typography color="inherit" variant="body1" mb={0.75}>
                                                settings
                                            </Typography>
                                        }
                                    />
                                    {settingsExpanded ? (
                                        <ExpandLess sx={{ color: 'inherit' }} />
                                    ) : (
                                        <ExpandMore sx={{ color: 'inherit' }} />
                                    )}
                                </ListItem>
                                <Collapse in={settingsExpanded} timeout="auto" unmountOnExit>
                                    <List
                                        component="div"
                                        disablePadding
                                        dense
                                        sx={{ pl: 6 }}
                                        data-testid="access-center-nav-settings-submenu"
                                        role="list"
                                    >
                                        {showUsersAndRolesInSettingsNav && (
                                            <>
                                                <NavigationButton
                                                    title="users"
                                                    path={URLPaths.SETTINGS_USERS}
                                                    helperText="View and manage existing users and their assigned roles."
                                                    variant={NavigationButtonVariant.WIDE}
                                                    dataTestId="access-center-nav-users"
                                                    itemRole="menuitem"
                                                    selectedOverride={location.pathname === URLPaths.SETTINGS_USERS}
                                                />
                                                <NavigationButton
                                                    title="roles"
                                                    path={URLPaths.SETTINGS_ROLES}
                                                    helperText="Create and edit roles by selecting permissions."
                                                    variant={NavigationButtonVariant.WIDE}
                                                    dataTestId="access-center-nav-roles"
                                                    itemRole="menuitem"
                                                    selectedOverride={location.pathname === URLPaths.SETTINGS_ROLES}
                                                />
                                            </>
                                        )}
                                        {hasCustomerSupportFlag && !hasContentOnly && (
                                            <ListItem
                                                button
                                                onClick={() => {
                                                    dispatch(minimizeLeftNavPanel(false));
                                                    history.push(URLPaths.SETTINGS_CUSTOMER_SUPPORT);
                                                }}
                                                selected={location.pathname === URLPaths.SETTINGS_CUSTOMER_SUPPORT}
                                                sx={{
                                                    color:
                                                        location.pathname === URLPaths.SETTINGS_CUSTOMER_SUPPORT
                                                            ? 'secondary.main'
                                                            : 'primary.main',
                                                    height: '3.25rem',
                                                    '&:hover': { color: 'action.active' },
                                                }}
                                                data-testid="access-center-nav-customer-support"
                                                role="menuitem"
                                            >
                                                <ListItemText
                                                    disableTypography
                                                    primary={
                                                        <Typography color="inherit" variant="body1" mb={0.75}>
                                                            customer support
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                        )}
                                        {showBrandSetupInSettingsNav && !hasContentOnly && (
                                            <NavigationButton
                                                title="brand setup"
                                                path={URLPaths.SETTINGS_BRAND}
                                                helperText="Set up and manage your brand’s voice and tone"
                                                variant={NavigationButtonVariant.WIDE}
                                                dataTestId="access-center-nav-brand-setup"
                                                itemRole="menuitem"
                                                selectedOverride={
                                                    location.pathname === URLPaths.SETTINGS_BRAND ||
                                                    location.pathname === URLPaths.SETTINGS_BRAND_EDIT
                                                }
                                            />
                                        )}
                                    </List>
                                </Collapse>
                            </>
                        )}
                    </List>
                </Box>
                {/* navigation panel border when maximized */}
                <Box
                    height="100%"
                    width="0.25rem"
                    sx={{
                        backgroundColor: isHover ? 'secondary.containedHoverBackground' : 'secondary.main',
                        transitionDuration: '0.23s',
                        transitionProperty: 'background-color',
                        cursor: 'pointer',
                    }}
                    onMouseEnter={() => setIsHover(true)}
                    onMouseLeave={() => setIsHover(false)}
                    onClick={() => dispatch(minimizeLeftNavPanel(false))}
                    position="fixed"
                    left="15.75rem"
                    top={0}
                    zIndex={4}
                />
                {/* navigation panel toggle when maximized */}
                <Button
                    onClick={() => {
                        dispatch(minimizeLeftNavPanel(false));
                        setIsHover(false);
                    }}
                    onMouseOver={() => {
                        setIsHover(true);
                    }}
                    onMouseLeave={() => {
                        setIsHover(false);
                    }}
                    sx={{
                        backgroundColor: isHover ? 'secondary.containedHoverBackground' : 'secondary.main',
                        minWidth: '2rem',
                        maxWidth: '2rem',
                        minHeight: '2rem',
                        maxHeight: '2rem',
                        padding: '0rem',
                        borderRadius: '0.25rem 0 0 0.25rem',
                        position: 'fixed',
                        bottom: '4rem',
                        left: '13.75rem',
                        zIndex: 5,
                        '&:hover': {
                            backgroundColor: 'secondary.containedHoverBackground',
                        },
                    }}
                >
                    <ArrowBackIosRounded sx={{ color: 'white', fontSize: '1rem' }} />
                </Button>
            </Box>
            {/* navigation panel when minimized */}
            <Box hidden={isLeftNavPanelMaximized}>
                <Box
                    height="100%"
                    width="4rem"
                    sx={{ backgroundColor: 'secondary.light' }}
                    position="fixed"
                    left={0}
                    top={0}
                    zIndex={5}
                    overflow="hidden"
                    flex={1}
                >
                    <List dense disablePadding>
                        <ProfileButton userEmail={user.email} />
                        <Box
                            display="flex"
                            flexDirection="row"
                            justifyContent="center"
                            height="100%"
                            marginTop={2}
                            marginBottom={10}
                        >
                            <Link to="/">
                                <img
                                    src={CuredLogoNoText}
                                    alt="Cured logo"
                                    width="32"
                                    height="32"
                                    onClick={() => dispatch(minimizeLeftNavPanel(false))}
                                />
                            </Link>
                        </Box>
                        {!hasContentOnly && showCurationsNavItem && (
                            <NavigationButton
                                path={URLPaths.CURATIONS}
                                helperText="curations"
                                icon={<SwapCalls />}
                                variant={NavigationButtonVariant.NARROW}
                            />
                        )}
                        {!hasAccessControlLayer && !hasContentOnly && showAudiencesNavItem && (
                            <NavigationButton
                                path={URLPaths.SEGMENTS}
                                helperText="audiences"
                                icon={<SupervisedUserCircleOutlined />}
                                variant={NavigationButtonVariant.NARROW}
                            />
                        )}
                        {!hasAccessControlLayer && !hasContentOnly && showContactsNavItem && (
                            <NavigationButton
                                path={URLPaths.CONTACTS}
                                helperText="contacts"
                                icon={<ContactPageOutlined />}
                                variant={NavigationButtonVariant.NARROW}
                            />
                        )}
                        {showContentNavItem && (
                            <NavigationButton
                                path={URLPaths.CONTENT}
                                helperText="content"
                                icon={<ContentIcon />}
                                variant={NavigationButtonVariant.NARROW}
                                dataTestId="content-button-list-item"
                                ariaLabel="Open content"
                            />
                        )}
                        {!hasContentOnly && showValueNavItem && (
                            <NavigationButton
                                path={URLPaths.VALUE}
                                helperText="value"
                                icon={<PaidOutlined />}
                                variant={NavigationButtonVariant.NARROW}
                            />
                        )}
                        {!hasContentOnly && showInsightsNavItem && (
                            <NavigationButton
                                path={URLPaths.INSIGHTS}
                                helperText="insights"
                                icon={<AutoGraph />}
                                variant={NavigationButtonVariant.NARROW}
                            />
                        )}
                        {showSettingsNav && (
                            <NavigationButton
                                path="/settings"
                                helperText="settings"
                                icon={<SettingsOutlined />}
                                variant={NavigationButtonVariant.NARROW}
                                onClickOverride={() => {
                                    dispatch(maximizeLeftNavPanel());
                                    setSettingsExpanded(true);
                                }}
                                dataTestId="access-center-nav-settings"
                                ariaLabel="Open settings menu"
                            />
                        )}
                    </List>
                </Box>
                {/* navigation panel border when minimized */}
                <Box
                    height="100%"
                    width="0.25rem"
                    sx={{
                        backgroundColor: isHover ? 'secondary.containedHoverBackground' : 'secondary.main',
                        transitionDuration: '0.23s',
                        transitionProperty: 'background-color',
                        cursor: 'pointer',
                    }}
                    onMouseEnter={() => setIsHover(true)}
                    onMouseLeave={() => setIsHover(false)}
                    onClick={() => dispatch(maximizeLeftNavPanel())}
                    position="fixed"
                    left="4rem"
                    top={0}
                    zIndex={4}
                />
                {/* navigation panel toggle when minimized */}
                <Button
                    onClick={() => {
                        dispatch(maximizeLeftNavPanel());
                        setIsHover(false);
                    }}
                    onMouseOver={() => {
                        setIsHover(true);
                    }}
                    onMouseLeave={() => {
                        setIsHover(false);
                    }}
                    sx={{
                        backgroundColor: isHover ? 'secondary.containedHoverBackground' : 'secondary.main',
                        minWidth: '2rem',
                        maxWidth: '2rem',
                        minHeight: '2rem',
                        maxHeight: '2rem',
                        padding: '0rem',
                        borderRadius: '0 0.25rem 0.25rem 0',
                        position: 'fixed',
                        bottom: '4rem',
                        left: '4rem',
                        zIndex: 5,
                        '&:hover': {
                            backgroundColor: 'secondary.containedHoverBackground',
                        },
                    }}
                >
                    <ArrowForwardIosRounded sx={{ color: 'white', fontSize: '1rem' }} />
                </Button>
            </Box>
        </>
    );
};

export default NavigationPanel;
